/**
 * Phase 4g.E (Feb 2026) — KV-Versand-Modal extrahiert aus `WorkshopOrdersPage.jsx`.
 * Jun-2026 (User-Wunsch) — Versand zusätzlich per WhatsApp & SMS (Annahme-Link),
 * Standard-Begleittext voreingeblendet, Button „Kostenvoranschlag versenden".
 */
import { useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check, Mail, MessageCircle, Smartphone, X, Send } from "lucide-react";

import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import EmailTextEditor from "../dealer/EmailTextEditor";

function SendQuoteMailModal({ order, customer, onClose, onSent }) {
    const custName = customer?.company_name
        || [customer?.first_name, customer?.last_name].filter(Boolean).join(" ")
        || "";
    const defaultMessage = `Guten Tag${custName ? " " + custName : ""},\n\nanbei erhalten Sie Ihren Kostenvoranschlag. Über den Link können Sie ihn ansehen und direkt annehmen. Bei Fragen stehen wir Ihnen gerne zur Verfügung.\n\nMit freundlichen Grüßen`;

    const [mobileChannel, setMobileChannel] = useState("none"); // "none" | "whatsapp" | "sms" — exklusiv
    const [toEmail, setToEmail] = useState(customer?.email || "");
    const [toPhone, setToPhone] = useState(customer?.phone || customer?.phone_mobile || "");
    const [validDays, setValidDays] = useState(14);
    const [customMessage, setCustomMessage] = useState(defaultMessage);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const send = async (e) => {
        e.preventDefault();
        // Systemweite Regel (Jun-2026): E-Mail immer, zusätzlich WhatsApp ODER SMS (exklusiv).
        if (!toEmail.trim()) { toast.error("E-Mail-Adresse fehlt (E-Mail wird immer versendet)"); return; }
        if (mobileChannel !== "none" && !toPhone.trim()) { toast.error("Telefonnummer fehlt"); return; }
        setBusy(true);
        try {
            const r = await api.post(`/work-orders/${order.id}/send-quote-email`, {
                channel: "email",
                to_email: toEmail.trim() || undefined,
                to_phone: toPhone.trim() || undefined,
                valid_days: Number(validDays) || 14,
                custom_message: customMessage.trim() || undefined,
            });
            let rMobile = null;
            if (mobileChannel !== "none") {
                rMobile = await api.post(`/work-orders/${order.id}/send-quote-email`, {
                    channel: mobileChannel,
                    to_email: toEmail.trim() || undefined,
                    to_phone: toPhone.trim() || undefined,
                    valid_days: Number(validDays) || 14,
                    custom_message: customMessage.trim() || undefined,
                });
                if (mobileChannel === "whatsapp" && rMobile.data.wa_url) {
                    window.open(rMobile.data.wa_url, "_blank", "noopener");
                }
            }
            const extra = mobileChannel === "whatsapp" ? " + WhatsApp" : mobileChannel === "sms" ? " + SMS" : "";
            toast.success(`Kostenvoranschlag versendet (E-Mail${extra})`);
            setResult(rMobile?.data || r.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Versand fehlgeschlagen");
        }
        setBusy(false);
    };

    const close = () => { if (result) onSent(); else onClose(); };

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="quote-mail-modal">
            <div className="bg-white rounded-sm w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <h2 className="font-display font-black text-lg flex items-center gap-2">
                        <Send className="w-5 h-5 text-amber-700" />
                        Kostenvoranschlag versenden · {order.order_number}
                    </h2>
                    <button onClick={close} className="p-1 rounded-sm hover:bg-gray-100" data-testid="quote-mail-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {!result ? (
                    <form onSubmit={send} className="p-4 space-y-3">
                        <p className="text-sm text-gray-600">
                            Der Kunde erhält den Kostenvoranschlag mit einem Annahme-Link. Bei Klick wird der Auftrag automatisch auf <strong>„Auftrag"</strong> (confirmed) gesetzt.
                        </p>

                        {/* E-Mail immer + WhatsApp ODER SMS (systemweite Regel) */}
                        <div data-testid="quote-mail-channels">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700 flex items-center gap-1.5">
                                    <Mail className="w-3.5 h-3.5" /> Empfänger E-Mail
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 ml-1">(immer)</span>
                                </label>
                                <Input type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)}
                                    className="rounded-sm mt-1" placeholder="kunde@example.de" data-testid="quote-mail-to" />
                            </div>
                            <div className="mt-3">
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Zusätzlich mobil — eines wählen:</div>
                                <div className="flex flex-wrap gap-4">
                                    <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                        <input type="radio" name="quote-mobile" checked={mobileChannel === "none"} onChange={() => setMobileChannel("none")} className="w-4 h-4 accent-[#111827]" data-testid="quote-mail-channel-none" />
                                        Nur E-Mail
                                    </label>
                                    <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                        <input type="radio" name="quote-mobile" checked={mobileChannel === "whatsapp"} onChange={() => setMobileChannel("whatsapp")} className="w-4 h-4 accent-[#25D366]" data-testid="quote-mail-channel-whatsapp" />
                                        <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                                    </label>
                                    <label className="inline-flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                                        <input type="radio" name="quote-mobile" checked={mobileChannel === "sms"} onChange={() => setMobileChannel("sms")} className="w-4 h-4 accent-[#111827]" data-testid="quote-mail-channel-sms" />
                                        <Smartphone className="w-3.5 h-3.5" /> SMS
                                    </label>
                                </div>
                            </div>
                        </div>

                        {mobileChannel !== "none" && (
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Empfänger Telefon</label>
                                <Input type="tel" value={toPhone} onChange={(e) => setToPhone(e.target.value)}
                                    className="rounded-sm mt-1" placeholder="+49 170 1234567" data-testid="quote-mail-phone" />
                                <p className="text-[11px] text-gray-400 mt-1">
                                    {mobileChannel === "whatsapp" ? "WhatsApp öffnet sich mit vorbereiteter Nachricht inkl. Annahme-Link." : "Es wird zusätzlich eine SMS mit dem Annahme-Link versendet."}
                                </p>
                            </div>
                        )}

                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-gray-700">Gültigkeit (Tage)</label>
                            <Input type="number" min="1" max="180" value={validDays} onChange={(e) => setValidDays(e.target.value)}
                                className="rounded-sm mt-1" data-testid="quote-mail-valid-days" />
                        </div>

                        <div className="pt-1">
                            <EmailTextEditor
                                value={customMessage}
                                onChange={setCustomMessage}
                                defaultText={defaultMessage}
                                label="Begleittext ansehen / ändern"
                                immutableHint="Fahrzeug, Gesamtbetrag, Gültigkeit, der Annahme-Link und (bei E-Mail) das KV-PDF werden automatisch angehängt."
                                testid="quote-mail-text"
                            />
                        </div>

                        <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded-sm text-xs text-amber-900 flex gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0" />
                            <div>Ohne <code>RESEND_API_KEY</code> läuft der Versand im DEMO-Modus (Inhalt wird im Backend-Log protokolliert, keine echte Mail).</div>
                        </div>

                        <Button type="submit" disabled={busy}
                            className="w-full bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider"
                            data-testid="quote-mail-submit">
                            {busy ? "Sende…" : "Kostenvoranschlag versenden"}
                        </Button>
                    </form>
                ) : (
                    <div className="p-4 space-y-3" data-testid="quote-mail-result">
                        <div className="flex items-center gap-2 text-green-700">
                            <Check className="w-6 h-6" />
                            <div className="font-display font-black text-lg">
                                {result.mocked ? "Versand (DEMO) protokolliert" : "Versendet"}
                            </div>
                        </div>
                        <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-xs space-y-1">
                            <div className="flex justify-between"><span className="text-gray-500">Empfänger</span><span className="font-bold font-mono-num">{result.sent_to || result.to_email}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Gültig bis</span><span className="font-bold">{result.valid_until}</span></div>
                            {result.accept_url && (
                                <div className="pt-2 border-t border-gray-200">
                                    <div className="text-gray-500 mb-1">Annahme-Link (zum Testen oder Kunden manuell senden)</div>
                                    <a href={result.accept_url} target="_blank" rel="noopener noreferrer"
                                        className="text-blue-700 hover:underline break-all text-[11px] font-mono-num" data-testid="quote-mail-accept-link">
                                        {result.accept_url}
                                    </a>
                                </div>
                            )}
                        </div>
                        <Button onClick={onSent} className="w-full bg-[#111827] hover:bg-black text-white rounded-sm uppercase tracking-wider" data-testid="quote-mail-done">
                            Schließen
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default SendQuoteMailModal;
